// TCPA quiet hours — the front-end mirror of the server clamp.
//
// The authoritative clamp lives in supabase/functions/_shared/timezone.ts
// (clampToTcpaHours, applied inside loadTenantHours). This file exists so the
// settings UI can stop a tenant from PICKING an illegal window in the first
// place, and so screens can tell a tenant their stored window is being
// overridden. Keep the two bounds identical to the server's.
//
// Why both: the server clamp is what actually protects leads, and it must never
// be removed in favour of form validation. But a form that silently accepts 6 AM
// and then behaves as 8 AM is its own bug — the tenant reads the number back and
// believes it. So: server enforces, UI explains.
//
// Federal TCPA permits marketing texts 8:00 AM – 9:00 PM in the recipient's local
// time. LATEST_HOUR is EXCLUSIVE, matching the server's `hour < closeHour`, so 21
// means the last message can leave at 8:59 PM.

export const TCPA_EARLIEST_HOUR = 8;
export const TCPA_LATEST_HOUR = 21;

/** Human label for a whole-hour value, e.g. 8 -> "8:00 AM", 21 -> "9:00 PM". */
export function formatHour(hour) {
  const h = Number(hour);
  if (!Number.isFinite(h)) return '';
  const suffix = h >= 12 ? 'PM' : 'AM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${suffix}`;
}

export const TCPA_WINDOW_LABEL = `${formatHour(TCPA_EARLIEST_HOUR)} to ${formatHour(TCPA_LATEST_HOUR)}`;

/**
 * Does this configured window fall outside TCPA hours?
 * Blank / unparseable values are NOT flagged — they mean "inherit", and the
 * inherited value gets checked on its own.
 */
export function isOutOfTcpaBounds(openHour, closeHour) {
  const o = Number(openHour);
  const c = Number(closeHour);
  const openBad = Number.isFinite(o) && String(openHour).trim() !== '' && o < TCPA_EARLIEST_HOUR;
  const closeBad = Number.isFinite(c) && String(closeHour).trim() !== '' && c > TCPA_LATEST_HOUR;
  return openBad || closeBad;
}

/**
 * What the server will actually use, given what the tenant configured.
 * Mirrors clampToTcpaHours in _shared/timezone.ts, including the fallback to the
 * 8–17 default for overnight or fully-illegal windows.
 */
export function clampToTcpaHours(openHour, closeHour) {
  const DEFAULT_OPEN = 8;
  const DEFAULT_CLOSE = 17;
  const o = Number(openHour);
  const c = Number(closeHour);
  if (!Number.isFinite(o) || !Number.isFinite(c)) {
    return { openHour: o, closeHour: c, clamped: false };
  }
  if (o > c) return { openHour: DEFAULT_OPEN, closeHour: DEFAULT_CLOSE, clamped: true };
  const co = Math.max(o, TCPA_EARLIEST_HOUR);
  const cc = Math.min(c, TCPA_LATEST_HOUR);
  if (co >= cc) return { openHour: DEFAULT_OPEN, closeHour: DEFAULT_CLOSE, clamped: true };
  return { openHour: co, closeHour: cc, clamped: co !== o || cc !== c };
}
